import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Target, ArrowRight } from 'lucide-react';
import { useRole, ROLES } from '../../contexts/RoleContext';

const RoleSelectionPage = () => {
  const navigate = useNavigate();
  const { setRole, ROLE_CONFIG } = useRole();

  const handleRoleSelection = (role) => {
    setRole(role);
    const config = ROLE_CONFIG[role];
    navigate(`/gps${config.routePrefix}/dashboard`);
  };

  const roleOptions = [
    {
      role: ROLES.LEADER,
      icon: Target,
      title: 'Leader Assessments',
      description: 'Upload and manage leader GPS assessments',
      features: [
        'Upload leader assessment files',
        'Assign assessments to leaders',
        'Track leader completion',
        'View leader analytics',
        'Manage leader benchmarks'
      ],
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    },
    {
      role: ROLES.TEAM_MEMBER,
      icon: Users,
      title: 'Team Member Assessments',
      description: 'Upload and manage team member GPS assessments',
      features: [
        'Upload team member assessment files',
        'Assign assessments to team members',
        'Track team member completion',
        'View team analytics',
        'Manage team benchmarks'
      ],
      color: '#6366f1',
      gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    }
  ];

  return (
    <div className="assessment-details-container">
      {/* Header - Centered */}
      <div className="assessment-header">
        <div className="container">
          <a 
            href="/"
            className="assessment-back-button"
          >
            ← Back to Home
          </a>
          <div className="text-center">
            <h1 className="assessment-title">
              Operational Excellence
            </h1>
            <p className="assessment-subtitle">
              GPS Assessment System - Choose Your Role
            </p>
          </div>
        </div>
      </div>

      {/* Role Selection Cards */}
      <div className="assessment-content">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {roleOptions.map((option) => {
            const IconComponent = option.icon;
            
            return (
              <div
                key={option.role}
                className="role-selection-card text-center"
                style={{
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '24px',
                  padding: '3rem',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.08)';
                }}
              >
                {/* Gradient accent border */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: option.gradient
                  }}
                />

                {/* Icon - Centered */}
                <div
                  className="mb-6 mx-auto"
                  style={{
                    width: '5rem',
                    height: '5rem',
                    borderRadius: '20px',
                    background: option.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '2rem',
                    boxShadow: `0 8px 20px ${option.color}30`
                  }}
                >
                  <IconComponent size={36} color="white" />
                </div>

                {/* Content - Centered */}
                <h2
                  className="text-3xl font-bold mb-4"
                  style={{ color: '#111827' }}
                >
                  {option.title}
                </h2>

                <p
                  className="text-lg mb-8"
                  style={{ color: '#6b7280', lineHeight: '1.6' }}
                >
                  {option.description}
                </p>

                {/* Features - Left aligned but centered container */}
                <div className="mb-10">
                  <ul className="space-y-4 text-left inline-block">
                    {option.features.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-center text-base"
                        style={{ color: '#374151' }}
                      >
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: option.color,
                            marginRight: '12px',
                            flexShrink: 0
                          }}
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Spacer for better button positioning */}
                <div style={{ marginBottom: '1.5rem' }}></div>

                {/* Modern Action Button */}
                <button
                  onClick={() => handleRoleSelection(option.role)}
                  className="modern-role-button"
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px 32px',
                    borderRadius: '16px',
                    fontSize: '18px',
                    fontWeight: '600',
                    letterSpacing: '0.025em',
                    background: option.gradient,
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: `0 4px 14px ${option.color}40`
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = `0 8px 25px ${option.color}50`;
                    e.target.style.background = `linear-gradient(135deg, ${option.color} 0%, ${option.color}dd 100%)`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = `0 4px 14px ${option.color}40`;
                    e.target.style.background = option.gradient;
                  }}
                  onMouseDown={(e) => {
                    e.target.style.transform = 'translateY(0) scale(0.98)';
                  }}
                  onMouseUp={(e) => {
                    e.target.style.transform = 'translateY(-2px) scale(1)';
                  }}
                >
                  <span>Access {option.title}</span>
                  <ArrowRight 
                    size={20} 
                    style={{ 
                      marginLeft: '12px',
                      transition: 'transform 0.3s ease'
                    }} 
                  />
                </button>
              </div>
            );
          })}
        </div>


      </div>
    </div>
  );
};

export default RoleSelectionPage;